// The guided story model: an author-friendly JSON document that the story
// compiler turns into a real ink script for the engine. Authors compose this
// through the story editor (forms, no ink syntax required).

export type StorySchema = "linear" | "roguelike";

export type SceneKind = "narrative" | "floor" | "ending";

export type VarValue = string | number | boolean;

export interface StoryModel {
  version: 1;
  schema: StorySchema;
  start: string;
  variables: Record<string, VarValue>;
  scenes: StoryScene[];
  messages: Record<string, string>;
}

export interface StoryScene {
  id: string;
  title: string;
  kind: SceneKind;
  sceneKey: string;
  color: string;
  tier: 1 | 2 | 3;
  tags: string[];
  lines: string[];
  choices: SceneChoice[];
}

export interface SceneChoice {
  id: string;
  label: string;
  condition: string;
  set: Record<string, VarValue>;
  add: Record<string, VarValue>;
  lines: string[];
  tags: string[];
  target: string | null;
}

export interface StoryMeta {
  title: string;
  subtitle: string;
  description: string;
  coverEmoji: string;
  coverColor: string;
}

export function emptyModel(schema: StorySchema = "roguelike"): StoryModel {
  return {
    version: 1,
    schema,
    start: "",
    variables: schema === "roguelike" ? { health: 100 } : {},
    scenes: [],
    messages: {},
  };
}

const slugRe = /[^a-z0-9_]/gi;

export function slugify(input: string): string {
  const cleaned = input.trim().toLowerCase().replace(slugRe, "_");
  const trimmed = cleaned.replace(/^_+|_+$/g, "");
  return trimmed || "scene";
}

export function newSceneId(title: string, taken: Set<string>): string {
  const base = slugify(title);
  let candidate = base;
  let i = 2;
  while (taken.has(candidate)) {
    candidate = `${base}_${i}`;
    i += 1;
  }
  taken.add(candidate);
  return candidate;
}

export function newChoiceId(): string {
  return `c_${Math.random().toString(36).slice(2, 9)}`;
}

// Character sheets are stored separately (reusable) but attached to a story
// as a cast. The story model keeps the references; the full character sheets
// are resolved at play time by the game wrapper.

export interface CharacterStat {
  label: string;
  value: string;
}

export interface CharacterSheet {
  id: string;
  title: string;
  role: string;
  blurb: string;
  avatarEmoji: string;
  color: string;
  stats: CharacterStat[];
  traits: string[];
  tags: string[];
  public: boolean;
}
